import { useQuery, useQueries, useMutation, useQueryClient } from 'react-query'
import client, { multipartclient, deleteclient } from './api-client'

const queryConfig = {
  staleTime: 1000 * 60 * 60,
  cacheTime: 1000 * 60 * 60,
}

async function fetchParcelImagesDates(
  parcel_id,
  layer,
  maxcc,
  from_date,
  to_date
) {
  const data = await client(
    `catalog/parcel_images_dates/?parcel_id=${parcel_id}&layer=${layer}&maxcc=${maxcc}&from_date=${from_date}`
  )
  return data
}

export function useParcelImagesDates(
  parcel_id,
  layer,
  maxcc,
  from_date,
  to_date
) {
  return useQuery(
    ['images_dates', parcel_id, layer, maxcc, from_date, to_date],
    () =>
      fetchParcelImagesDates(parcel_id, layer, maxcc, from_date, to_date),
    {
      refetchAllOnWindowFocus: false,
      enabled: false,
      ...queryConfig,
    }
  )
}

async function fetchParcelDates(parcel_id, layer, maxcc, from_date, to_date) {
  const data = await client(
    `catalog/parcel_dates/?parcel_id=${parcel_id}&layer=${layer}&maxcc=${maxcc}&from_date=${from_date}`
  )
  return data
}

export function useParcelDates(parcel_id, layer, maxcc, from_date, to_date) {
  return useQuery(
    ['images_dates', parcel_id, layer, maxcc, from_date, to_date],
    () => fetchParcelDates(parcel_id, layer, maxcc, from_date, to_date),
    {
      refetchAllOnWindowFocus: false,
      ...queryConfig,
    }
  )
}

async function fetchParcelFIS(parcel_id, layer, maxcc, date) {
  const data = await client(
    `catalog/parcel_fis/?parcel_id=${parcel_id}&layer=${layer}&maxcc=${maxcc}&date=${date}`
  )
  return data
}

export function useParcelFIS(parcel_id, layer, maxcc, date) {
  return useQuery(
    ['parcel_fis', parcel_id, layer, maxcc, date],
    () => fetchParcelFIS(parcel_id, layer, maxcc, date),
    {
      refetchAllOnWindowFocus: false,
      enabled: false,
      ...queryConfig,
    }
  )
}

async function fetchParcelFISHistory(
  parcel_id,
  layer,
  maxcc,
  from_date,
  to_date
) {
  const data = await client(
    `catalog/parcel_fis_history/?parcel_id=${parcel_id}&layer=${layer}&maxcc=${maxcc}&from=${from_date}&to=${to_date}`
  )
  return data
}

export function useParcelFISHistory(
  parcel_id,
  layer,
  maxcc,
  from_date,
  to_date
) {
  return useQuery(
    ['parcel_fis_history', parcel_id, layer, maxcc, from_date, to_date],
    () =>
      fetchParcelFISHistory(parcel_id, layer, maxcc, from_date, to_date),
    {
      refetchAllOnWindowFocus: false,
      enabled: false,
      ...queryConfig,
    }
  )
}

export async function fetchParcelStatistics(
  parcel_id,
  layer,
  maxcc,
  from_date,
  to_date
) {
  const data = await client(
    `parcels/${parcel_id}/layers/${layer}/statistics/?from_date=${from_date}&to_date=${to_date}&maxcc=${maxcc}`  )
  return data?.data
}

export function useParcelStatistics(
  parcel_id,
  layer,
  maxcc,
  from_date,
  to_date
) {
  const queryResult = useQuery(
    ['parcel_fis_history', parcel_id, layer, maxcc, from_date, to_date],
    () =>
      fetchParcelStatistics(parcel_id, layer, maxcc, from_date, to_date),
    {
      refetchAllOnWindowFocus: false,
      enabled: false,
      ...queryConfig,
    }
  )

  return {
    ...queryResult,
    isLoading: queryResult.isLoading,
    data: queryResult.data,
    error: queryResult.error,
    refetch: queryResult.refetch,
  }
}

export function useComparisonLayersStatistics(
  parcel_id,
  layers,
  from_date,
  to_date
) {
  const queries = useQueries(
    (layers || []).map((layer) => ({
      queryKey: ['comparison_layer_stats', parcel_id, layer.key, from_date, to_date],
      queryFn: () => fetchParcelStatistics(parcel_id, layer.key, 1, from_date, to_date),
      enabled: !!parcel_id && !!layer.key,
      ...queryConfig,
    }))
  )

  const isLoading = queries.some((q) => q.isLoading)
  const dataByLayer = {}
  ;(layers || []).forEach((layer, index) => {
    if (queries[index]?.data) {
      dataByLayer[layer.key] = queries[index].data
    }
  })

  return { dataByLayer, isLoading }
}

async function fetchParcelPlantsStatistics(
  parcel_id,
  layer,
  from_date,
  to_date,
  nbins = 2
) {
  const data = await client(
    `parcels/${parcel_id}/layers/${layer}/plants-statistics/?from_date=${from_date}&to_date=${to_date}&nbins=${nbins}`
  )
  return data?.data
}

export function useParcelPlantsStatistics(
  parcel_id,
  layer,
  from_date,
  to_date,
  nbins = 2
) {
  const queryResult = useQuery(
    ['parcel_plants_statistics', parcel_id, layer, from_date, to_date, nbins],
    () =>
      fetchParcelPlantsStatistics(parcel_id, layer, from_date, to_date, nbins),
    {
      refetchAllOnWindowFocus: false,
      enabled: false,
      ...queryConfig,
    }
  )

  return {
    ...queryResult,
    isLoading: queryResult.isLoading,
    data: queryResult.data,
    error: queryResult.error,
    refetch: queryResult.refetch,
  }
}

async function fetchParcelFISBrief(
  parcel_id,
  layer,
  maxcc,
  from_date,
  to_date
) {
  const data = await client(
    `catalog/parcel_fis_brief/?parcel_id=${parcel_id}&layer=${layer}&maxcc=${maxcc}&from=${from_date}&to=${to_date}`
  )
  return data
}

export function useParcelFISBrief(parcel_id, layer, maxcc, from_date, to_date) {
  return useQuery(
    ['parcel_brief_fis', parcel_id, layer, maxcc, from_date, to_date],
    () => fetchParcelFISBrief(parcel_id, layer, maxcc, from_date, to_date),
    {
      refetchAllOnWindowFocus: false,
      enabled: false,
      ...queryConfig,
    }
  )
}

async function fetchParcelZoning(
  parcel_id,
  layer,
  date,
  end_date,
  zones,
  maxcc
) {
  const data = await client(
    `catalog/zoning/?parcel_id=${parcel_id}&layer=${layer}&date=${date}&zones=${zones}&maxcc=${maxcc}&end_date=${end_date}`
  )
  return data
}

export function useParcelZoning(
  parcel_id,
  layer,
  date,
  end_date,
  zones,
  maxcc
) {
  return useQuery(
    ['zoning', parcel_id, layer, date, end_date, zones, maxcc],
    () => fetchParcelZoning(parcel_id, layer, date, end_date, zones, maxcc),
    {
      refetchAllOnWindowFocus: false,
      enabled: false,
      ...queryConfig,
    }
  )
}

async function fetchParcelZoningKmean(params) {
  const data = await client(`catalog/zoning_kmean/`, { body: params })
  return data
}

export function useParcelZoningKmean(params) {
  return useQuery(
    ['zoning_kmean', params],
    () => fetchParcelZoningKmean(params),
    {
      refetchAllOnWindowFocus: false,
      enabled: false,
      ...queryConfig,
    }
  )
}

async function fetchCloudsLayer(user, farms) {
  const farmsParamList = farms.map((fId) => `&farm_id=${fId}`).join('')
  const data = await client(
    `catalog/clustered_parcels/?client_id=${user.id}${farmsParamList}`
  )
  return data
}

export function useCloudsLayer(user, farms, options = {}) {
  return useQuery(
    ['clouds_layer', user.username, farms],
    () => fetchCloudsLayer(user, farms),
    {
      refetchAllOnWindowFocus: false,
      //enabled: false,
      ...queryConfig,
      ...options,
    }
  )
}

async function geofileHead(files) {
  const data = await multipartclient('catalog/geofile_head/', { body: files })
  return data
}

export function useGeofileHead(files) {
  const queryClient = useQueryClient()

  return useMutation((files) => geofileHead(files))
}

async function performanceZoning(files) {
  const data = await client('catalog/zoning_performance_map/', {
    body: files,
  })
  return data
}

export function usePerformanceZoning(files) {
  const queryClient = useQueryClient()

  return useMutation((files) => performanceZoning(files))
}


async function fetchDownloadTiffs(params) {
  const data = await client(`catalog/download_tiffs/`, { body: params })
  return data
}

export function useDownloadTiffs() {
  return useMutation((params) => fetchDownloadTiffs(params))
}

async function fetchTiffsHistory(parcel_id) {
  const data = await client(`catalog/tiffs_history/?parcel_id=${parcel_id}`)
  return data
}

export function useTiffsHistory(parcel_id) {
  return useQuery(
    ['tiff_history', parcel_id],
    () =>
      fetchTiffsHistory(parcel_id),
    {
      refetchAllOnWindowFocus: false,
      enabled: true,
      ...queryConfig,
    }
  )
}


async function fetchFeatureStats(lat, lon, parcel_id, layer, maxcc, date) {
  const data = await client(
    `catalog/get_feature_stats/?lat=${lat}&lon=${lon}&parcel_id=${parcel_id}&layer=${layer}&maxcc=${maxcc}&date=${date}`
  )
  return data
}

export function useFetchFeatureStats(featureData) {
  return useQuery(
    ['feature_stats', featureData?.coordinates, featureData?.parcel_id, featureData?.layer, featureData?.maxcc, featureData?.date],
    () => fetchFeatureStats(featureData?.coordinates.lat, featureData?.coordinates.lng, featureData?.parcel_id, featureData?.layer, featureData?.maxcc, featureData?.date),
    {
      refetchAllOnWindowFocus: false,
      enabled: !!featureData?.coordinates && !!featureData?.parcel_id && !!featureData?.layer,
      ...queryConfig,
    }
  )
}


async function fetchFeatureUrl(url) {
  const response = await fetch(url);
  const data = await response.json();

  return data
}

export function useFeatureUrl(url) {
  return useQuery(
    ['url', url],
    () => fetchFeatureUrl(url),
    {
      refetchAllOnWindowFocus: false,
      enabled: !!url,
      ...queryConfig,
    }
  )
}
